<script setup lang="ts">
import { computed, ref } from 'vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import ExpandToggleButton from '@/_core/shared/ui/ExpandToggleButton/ExpandToggleButton.vue';
import ExpandableVulnerabilitySection from '../ExpandableVulnerabilitySection/ExpandableVulnerabilitySection.vue';
import VulnerabilityCodeBlock from '../VulnerabilityCodeBlock/VulnerabilityCodeBlock.vue';
import VulnerabilityPOC from '../VulnerabilityPOC/VulnerabilityPOC.vue';
import TrialOverlay from '../TrialOverlay/TrialOverlay.vue';
import { trackVulnSectionExpanded } from '@/services/analytics';
import StackTraceViewer from '@/components/scans/StackTraceViewer.vue';
import VulnAttachmentList from '@/components/scans/VulnAttachmentList.vue';
import { TReport } from '@/_core/entities/report/models/types';
import dayjs from 'dayjs';
import type {
  TVulnerabilityCodeLine,
  TVulnerabilityDetails,
} from '@/_core/entities/vulnerability/models/types';
import {
  trialMockCodeBlock,
  trialMockProofOfConcepts,
  trialMockStackTraces,
  trialMockAttachments,
} from '@/_core/entities/vulnerability/models/mock.data';

const props = defineProps<{
  vulnerability: TVulnerabilityDetails;
  reportData: TReport | undefined;
}>();

function getCodeBlockLeadingSpacesNumber(lines: TVulnerabilityCodeLine[]): number {
  if (lines.length === 0) return 0;
  return lines.reduce((min, line) => {
    const leading = line.content.match(/^(\s*)/)?.[1].length ?? 0;
    return Math.min(min, leading);
  }, 100);
}

const codeBlocks = computed(
  () =>
    props.vulnerability.blocks?.map((block) => ({
      ...block,
      minSpaces: getCodeBlockLeadingSpacesNumber(block.lines),
    })) || []
);

const expandedSections = ref({
  code: true,
  poc: true,
  stacktraces: true,
  attachments: true,
});

const allExpanded = computed(() => {
  return Object.values(expandedSections.value).every((s) => !!s);
});

function toggleAll() {
  expandedSections.value = {
    code: !allExpanded.value,
    poc: !allExpanded.value,
    stacktraces: !allExpanded.value,
    attachments: !allExpanded.value,
  };
}

const sectionAnalyticsNames: Record<string, string> = {
  code: 'code',
  poc: 'poc',
  stacktraces: 'stack_traces',
  attachments: 'attachments',
};

function handleSectionToggle(section: string, expanded: boolean) {
  (expandedSections.value as Record<string, boolean>)[section] = expanded;
  if (expanded) {
    trackVulnSectionExpanded(
      props.vulnerability.jobId,
      props.vulnerability.category.descriptionTitle,
      sectionAnalyticsNames[section] || section,
    );
  }
}

const showAllFiles = ref(false);

const isAttachmentsExpired = computed(() => {
  const days = props.reportData?.reportRetentionDays;
  const createdAt = props.reportData?.createdAt;
  if (!days || !createdAt) return false;
  return dayjs().isAfter(dayjs(createdAt).add(days, 'day'));
});
</script>

<template>
  <ReportSection>
    <div class="tw:flex tw:flex-col tw:gap-6 tw:lg:gap-8 tw:items-start">
      <div
        class="tw:w-full tw:flex tw:max-sm:flex-col tw:max-sm:items-start tw:gap-6 tw:md:justify-between"
      >
        <div class="tw:typography-h1">Details</div>

        <ExpandToggleButton
          :value="allExpanded"
          collapsed-text="Expand all sections"
          expanded-text="Collapse all sections"
          @input="toggleAll"
        />
      </div>

      <div class="tw:w-full tw:flex tw:flex-col tw:gap-6 tw:lg:gap-8">
        <ExpandableVulnerabilitySection
          v-if="
            vulnerability.hasCodeBlocks ||
            (vulnerability.isTrialIntegration && vulnerability.hasCodeBlocks)
          "
          title="Code"
          testing-method-text="sast"
          :expanded="expandedSections.code"
          @toggle="handleSectionToggle('code', $event)"
        >
          <TrialOverlay title="Code" v-if="vulnerability.isTrialIntegration && !codeBlocks.length">
            <VulnerabilityCodeBlock :vulnerability="vulnerability" :block="trialMockCodeBlock" />
          </TrialOverlay>

          <div class="tw:flex tw:flex-col tw:gap-5 tw:items-start" v-if="!!codeBlocks.length">
            <VulnerabilityCodeBlock :vulnerability="vulnerability" :block="codeBlocks[0]" />

            <template v-if="codeBlocks.length > 1">
              <template v-if="showAllFiles">
                <VulnerabilityCodeBlock
                  v-for="(block, index) in codeBlocks.slice(1)"
                  :key="index"
                  :vulnerability="vulnerability"
                  :block="block"
                />
              </template>

              <button @click="showAllFiles = !showAllFiles" class="tw:mt-4!">
                <div
                  class="tw:py-3 tw:px-4 tw:rounded-lg tw:typography-h3 tw:text-main-blue tw:bg-blue-bg tw:lg:hover:opacity-80 tw:transition-opacity"
                >
                  {{ showAllFiles ? 'Hide files' : 'Show all files' }}
                </div>
              </button>
            </template>
          </div>
        </ExpandableVulnerabilitySection>

        <ExpandableVulnerabilitySection
          v-if="
            vulnerability.hasProofOfConcepts ||
            (vulnerability.isTrialIntegration && vulnerability.hasProofOfConcepts)
          "
          title="Proof of Concept"
          testing-method-text="dast"
          :expanded="expandedSections.poc"
          @toggle="handleSectionToggle('poc', $event)"
        >
          <TrialOverlay
            title="Proof of Concept"
            v-if="
              vulnerability.isTrialIntegration &&
              vulnerability.hasProofOfConcepts &&
              !vulnerability.proofOfConcepts
            "
          >
            <VulnerabilityPOC
              :proof-of-concepts="trialMockProofOfConcepts"
              :severity="vulnerability.category.severity"
            />
          </TrialOverlay>
          <VulnerabilityPOC
            v-else
            :proof-of-concepts="vulnerability.proofOfConcepts || []"
            :severity="vulnerability.category.severity"
          />
        </ExpandableVulnerabilitySection>

        <ExpandableVulnerabilitySection
          v-if="
            vulnerability.hasStackTraces ||
            (vulnerability.isTrialIntegration && vulnerability.hasStackTraces)
          "
          title="Stack Trace"
          testing-method-text="dast"
          :expanded="expandedSections.stacktraces"
          @toggle="handleSectionToggle('stacktraces', $event)"
        >
          <TrialOverlay
            title="Stack Trace"
            v-if="vulnerability.isTrialIntegration && !vulnerability.stackTraces?.length"
          >
            <StackTraceViewer
              v-for="(item, index) in trialMockStackTraces"
              :key="index"
              :stack-trace-item="item"
              :stack-trace-index="index"
              :stack-traces-count="trialMockStackTraces.length"
              :severity="vulnerability.category.severity"
            />
          </TrialOverlay>
          <template v-else>
            <StackTraceViewer
              v-for="(item, index) in vulnerability.stackTraces"
              :key="index"
              :stack-trace-item="item"
              :stack-trace-index="index"
              :stack-traces-count="vulnerability.stackTraces.length"
              :severity="vulnerability.category.severity"
            />
          </template>
        </ExpandableVulnerabilitySection>

        <ExpandableVulnerabilitySection
          v-if="
            (!!vulnerability.hasAttachments && !isAttachmentsExpired) ||
            (vulnerability.isTrialIntegration && vulnerability.hasAttachments)
          "
          title="Attachments"
          testing-method-text="dast"
          :expanded="expandedSections.attachments"
          @toggle="handleSectionToggle('attachments', $event)"
        >
          <TrialOverlay
            title="Attachments"
            v-if="
              vulnerability.isTrialIntegration &&
              vulnerability.hasAttachments &&
              !vulnerability.attachments
            "
          >
            <VulnAttachmentList
              :attachments="trialMockAttachments"
              :job-id="vulnerability.jobId"
              :retention-days="reportData?.reportRetentionDays ?? undefined"
              :created-at="reportData?.createdAt"
            />
          </TrialOverlay>
          <div v-else class="tw:mt-2">
            <VulnAttachmentList
              v-if="!!vulnerability.attachments?.length"
              :attachments="vulnerability.attachments"
              :job-id="vulnerability.jobId"
              :retention-days="reportData?.reportRetentionDays ?? undefined"
              :created-at="reportData?.createdAt"
            />
          </div>
        </ExpandableVulnerabilitySection>
      </div>
    </div>
  </ReportSection>
</template>
